import React, { useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Flex } from "antd";

import {
  DataBlockDetailsStepProps,
  FormFieldsWithSharingRights,
} from "../../types/types";
import { SharingRights } from "@/shared/utils/constants";
import { useDataBlockDependencies } from "@/domains/datablocks/hooks/useDataBlockDependencies";
import FieldRenderer from "@/domains/datablocks/fields/FieldRenderer";

import "./styles.css";
import {
  ContentType,
  DataFieldResponse,
  DisplayType,
  FieldType,
  ValidationRuleTypes,
} from "@/redux/datablocks/types";

const DataBlockDetailsForm = ({
  dataTemplate,
  excludeFields = [],
}: DataBlockDetailsStepProps) => {
  const { t } = useTranslation();
  const { control } = useFormContext();
  const dataBlockDependencies = useDataBlockDependencies(dataTemplate);

  const fields: FormFieldsWithSharingRights[] = useMemo(() => {
    const newFields: FormFieldsWithSharingRights[] = [
      ...dataTemplate.templateFields.map((i: DataFieldResponse, index) => ({
        ...i,
        name: `fieldValues.${index}.value`, // must use index because RHF uses it to set it in the array
      })),
    ];
    newFields.splice(4, 0, {
      id: "sharingRights",
      name: "sharingRights",
      disabled: false,
      allowsCustomOptions: false,
      placeholder: t("sharingRights.selectPlaceholder"),
      contentType: ContentType.Options,
      options: SharingRights.asList().map((option: Record<string, string>) => ({
        id: option.value,
        label: option.label,
      })),
      order: 4,
      rules: [
        {
          type: ValidationRuleTypes.REQUIRED,
          dependentOn: null,
          errorMessage: "ruleRequired",
        },
      ],
      description: "",
      title: "Sharing Rights",
      type: FieldType.SELECT,
      displayType: DisplayType.DROPDOWN,
      multiple: false,
    });

    return newFields.filter((i) => !excludeFields.includes(i.id));
  }, [dataTemplate, excludeFields]);

  const eitherRequiredRules: Record<number, -1 | 0 | 1> = useMemo(() => {
    const rules = dataTemplate.validationRules
      .filter((rule) => rule.type === ValidationRuleTypes.EITHER_REQUIRED)
      .map((rule) => rule.ruleFields);

    const orderFieldIds = fields.map((field) => field.id);

    const orderedRules = rules.map((rule) =>
      rule
        .slice()
        .sort((a, b) => orderFieldIds.indexOf(a) - orderFieldIds.indexOf(b)),
    );

    return orderedRules.reduce(
      (acc, fieldIds) => {
        fieldIds.forEach((fieldId: number) => {
          const fieldIndex = fieldIds.indexOf(fieldId);
          acc[fieldId] =
            fieldIndex === 0 ? -1 : fieldIndex === fieldIds.length - 1 ? 1 : 0;
        });
        return acc;
      },
      {} as Record<number, -1 | 0 | 1>,
    );
  }, [dataTemplate, fields]);

  return (
    <Flex vertical gap={20}>
      {fields.map((field) => (
        <FieldRenderer
          key={`field-${field.id}-${dataTemplate.id}`}
          field={field}
          control={control}
          dataBlockDependencies={dataBlockDependencies}
          eitherRequiredRules={eitherRequiredRules}
        />
      ))}
    </Flex>
  );
};

export default React.memo(DataBlockDetailsForm);
